import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Stack, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
  InputAdornment, CircularProgress, MenuItem
} from "@mui/material";
import { Add, Edit, Delete, Search } from "@mui/icons-material";
import {
  collection, query, where, getDocs, addDoc, updateDoc,
  deleteDoc, doc, Timestamp, orderBy, getDoc
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from "sweetalert2";

function Dailydaybank() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolId, setSchoolId] = useState(null);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog State
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0], // New Date Field
    fieldName: "",
    fieldType: "Income",
    amount: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const sId = userSnap.data()?.schoolId;
      setSchoolId(sId);
      if (sId) {
        await fetchRecords(sId);
      }
    } catch (e) {
      console.error("Initialization Error:", e);
    }
  };

  const fetchRecords = async (sId) => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "dailydaybank"), 
        orderBy("date", "desc"), // Sorted primarily by transaction date
      );
      const snap = await getDocs(q);
      setRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Error fetching daybank records:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.date || !formData.fieldName || !formData.fieldType || !formData.amount) {
      return Swal.fire("Error", "All fields including Date are required", "error");
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount).toString(),
        schoolId,
        updatedAt: Timestamp.now(),
        createdAt: editId ? formData.createdAt : Timestamp.now()
      };

      if (editId) {
        await updateDoc(doc(db, "dailydaybank", editId), payload);
      } else {
        await addDoc(collection(db, "dailydaybank"), payload);
      }

      Swal.fire("Success", "Transaction record saved successfully", "success");
      setOpen(false);
      resetForm();
      fetchRecords(schoolId);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: 'Delete Bank Record?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete'
    });

    if (res.isConfirmed) {
      try {
        await deleteDoc(doc(db, "dailydaybank", id));
        fetchRecords(schoolId);
        Swal.fire("Deleted", "Record has been removed.", "success");
      } catch (e) {
        Swal.fire("Error", e.message, "error");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      fieldName: "",
      fieldType: "Income",
      amount: ""
    });
    setEditId(null);
  };

  const filteredRecords = records.filter(rec =>
    rec.fieldName?.toLowerCase().includes(search.toLowerCase()) ||
    rec.fieldType?.toLowerCase().includes(search.toLowerCase()) ||
    rec.date?.includes(search)
  );

  return (
    <Box p={1} bgcolor="#f8fafc" minHeight="100vh">
      {/* Dense Header Section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="primary.dark" sx={{ lineHeight: 1.2 }}>Daily Daybook & Bank</Typography>
          <Typography variant="caption" color="text.secondary">Manage day-to-day internal income, expenses and flow adjustments</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          size="small"
          onClick={() => { resetForm(); setOpen(true); }}
          sx={{ borderRadius: 1.5, px: 2, height: 32, fontSize: '13px', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
        >
          Add Record
        </Button>
      </Stack>

      {/* Dense Search Input Container */}
      <Card sx={{ p: 1, mb: 1.5, borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by field name, type, date (YYYY-MM-DD)..."
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            sx: { borderRadius: 1.5, height: 36, fontSize: '13px' }
          }}
        />
      </Card>

      {/* Dense Mini Compact Data Grid Table Layout */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#eff6ff" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Sr No</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Field Name</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Amount</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 2 }}><CircularProgress size={20} /></TableCell></TableRow>
            ) : filteredRecords.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, fontSize: '13px' }}>No records found.</TableCell></TableRow>
            ) : (
              filteredRecords.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((rec, index) => (
                <TableRow key={rec.id} hover>
                  <TableCell sx={{ py: 0.5, fontSize: '12.5px' }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: '12.5px' }}>{rec.date}</TableCell>
                  <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '12.5px' }}>{rec.fieldName}</TableCell>
                  <TableCell sx={{ py: 0.5, fontWeight: 700, fontSize: '12.5px' }}>
                    ₹{parseFloat(rec.amount).toLocaleString()}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton 
                        onClick={() => { 
                          setEditId(rec.id); 
                          setFormData({ ...rec }); 
                          setOpen(true); 
                        }} 
                        size="small" 
                        color="primary" 
                        sx={{ p: 0.5 }}
                      >
                        <Edit sx={{ fontSize: '16px' }} />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(rec.id)} size="small" color="error" sx={{ p: 0.5 }}>
                        <Delete sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ ".MuiTablePagination-toolbar": { minHeight: 36 }, ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: '12px' } }}
        />
      </TableContainer>

      {/* Entry Popup Dialog Form */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, p: 2, fontSize: '16px' }}>{editId ? "Update Record" : "Add New Record"}</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Stack spacing={2}>
            
            <TextField
              size="small"
              label="Date"
              type="date"
              fullWidth
              value={formData.date}
              onChange={e => setFormData({ ...formData, date: e.target.value })}
              InputLabelProps={{ shrink: true, style: { fontSize: 13 } }}
              inputProps={{ style: { fontSize: 13 } }}
            />

            <TextField
              size="small"
              label="Field Name"
              placeholder="e.g. Office Cash, Bank Account A"
              fullWidth
              value={formData.fieldName}
              onChange={e => setFormData({ ...formData, fieldName: e.target.value })}
              inputProps={{ style: { fontSize: 13 } }}
              InputLabelProps={{ style: { fontSize: 13 } }}
            />


            <TextField
              size="small"
              label="Amount"
              type="number"
              fullWidth
              value={formData.amount}
              onChange={e => setFormData({ ...formData, amount: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><span style={{ fontSize: 13 }}>₹</span></InputAdornment>, style: { fontSize: 13 } }}
              InputLabelProps={{ style: { fontSize: 13 } }}
            />

          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setOpen(false)} color="inherit" size="small" sx={{ fontSize: '13px' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            size="small"
            color="primary"
            sx={{ minWidth: 100, fontSize: '13px', height: 32 }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dailydaybank;